import { Link } from "react-router-dom";
import {
  NATIONAL_CONTEXT_ENTRIES,
  ACE_NATIONAL_LINE,
  MARBELLA_SELF_REFERENCE_LINE,
} from "@/data/national-context";

interface NationalContextSidebarProps {
  // The current city, so the sidebar can omit the reader's own city
  // from the outbound city list (no circular self-reference).
  currentCity: "Madrid" | "Barcelona" | "Valencia" | "Seville" | "Marbella";
}

export default function NationalContextSidebar({
  currentCity,
}: NationalContextSidebarProps) {
  const otherCities = NATIONAL_CONTEXT_ENTRIES.filter(
    (entry) => entry.city !== currentCity,
  );

  const aceLine =
    currentCity === "Marbella" ? MARBELLA_SELF_REFERENCE_LINE : ACE_NATIONAL_LINE;

  return (
    <aside
      className="mt-12 border-t border-b border-rule py-8"
      aria-label="National context — clinics elsewhere in Spain"
    >
      <div className="eyebrow mb-3">Nationally</div>
      <h2 className="font-serif text-h3 text-ink">
        If you&apos;re also considering clinics elsewhere in Spain
      </h2>
      <p className="mt-2 italic text-ink/80">
        — a city-by-city orientation —
      </p>

      <ul className="mt-6 space-y-5 text-small text-ink/90">
        {otherCities.map((entry) => (
          <li key={entry.city} className="flex gap-4">
            <span className="eyebrow w-28 shrink-0 pt-1">{entry.city}</span>
            <span className="flex-1">
              <Link
                to={entry.cityPath}
                className="font-medium text-ink underline decoration-rule underline-offset-4 hover:decoration-ink"
              >
                {entry.clinicName}
              </Link>
              {" "}&mdash;{" "}
              <span>strongest on {entry.strongestOn}</span>
              {" "}
              <a
                href={entry.clinicWebsite}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="font-sans text-xs uppercase tracking-wider text-charcoal/70 hover:text-ink"
              >
                (visit)
              </a>
            </span>
          </li>
        ))}

        <li className="flex gap-4 border-t border-rule/50 pt-5">
          <span className="eyebrow w-28 shrink-0 pt-1">
            Costa del Sol / Marbella
          </span>
          <span className="flex-1">
            {currentCity === "Marbella" ? (
              <span>{MARBELLA_SELF_REFERENCE_LINE.body}</span>
            ) : (
              <>
                <span className="font-medium text-ink">
                  {ACE_NATIONAL_LINE.clinicName}
                </span>
                {" "}&mdash;{" "}
                <span>{ACE_NATIONAL_LINE.body}</span>
                {" "}
                <a
                  href={ACE_NATIONAL_LINE.clinicWebsite}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="font-sans text-xs uppercase tracking-wider text-charcoal/70 hover:text-ink"
                >
                  (visit)
                </a>
              </>
            )}
          </span>
        </li>
      </ul>

      <p className="mt-8 font-sans text-small">
        <Link
          to="/best-smile-makeover-clinics-in-spain"
          className="underline decoration-rule underline-offset-4 hover:decoration-ink"
        >
          See the full national guide &rarr;
        </Link>
      </p>

      {/* Silence lint warning on unused helper variable while keeping
          import-level symmetry with the data file. */}
      <span className="hidden">{aceLine.city}</span>
    </aside>
  );
}
